/**
 * Path to Performance Slice State Helper Utilities
 * Reusable helpers for updating slice state to reduce duplication and complexity
 */

import type {
  PerformanceData,
  CurrentP2PData,
  TeamDetail,
} from "../../types/pathToPerformance";
import { mapHuddlesFromApi } from "./p2pDataFactory";
import { isDefined } from "../sliceHelpers";

/**
 * Performance data array index constant
 * P2P feature uses single-element arrays for performance data
 */
export const PERFORMANCE_DATA_INDEX = 0;

/**
 * Update performance data with P2P current data
 * Safely updates lastFaceToFaceAssessment and teamRating from API response
 * 
 * @param performanceData - Performance data to update
 * @param currentP2PData - Current P2P data from API
 */
export const updatePerformanceWithP2PData = (
  performanceData: PerformanceData | undefined,
  currentP2PData: CurrentP2PData,
): void => {
  if (!performanceData) return;

  // Update last face-to-face assessment date
  performanceData.lastFaceToFaceAssessment = 
    currentP2PData.learningPlanRefreshDate ?? "";

  // Update team rating if defined
  if (isDefined(currentP2PData.p2pRating)) {
    performanceData.teamRating = currentP2PData.p2pRating;
  }
};

/**
 * Update performance data with team aggregated data
 * Safely updates team details from API response
 * 
 * @param performanceData - Performance data to update
 * @param teamData - Team detail from API
 */
export const updatePerformanceWithTeamData = (
  performanceData: PerformanceData | undefined,
  teamData: TeamDetail,
): void => {
  if (!performanceData) return;

  performanceData.ubtName = teamData.teamName ?? "";
  performanceData.serviceType = teamData.serviceDeliveryType ?? "";
  performanceData.dateTeamEstablished = teamData.teamDateCreated ?? "";
  performanceData.usingHuddles = mapHuddlesFromApi(teamData.usingHuddles);

  // Update team rating if available
  if (teamData.teamRating) {
    performanceData.teamRating = teamData.teamRating;
  }
};

/**
 * Sync performance data between current and initial state
 * Updates both performanceData[0] and initialPerformanceData[0] with same values
 * 
 * @param performanceData - Current performance data array
 * @param initialPerformanceData - Initial performance data array
 * @param updater - Function to update a single performance data item
 * 
 * @example
 * ```typescript
 * syncPerformanceData(
 *   state.performanceData,
 *   state.initialPerformanceData,
 *   (data) => updatePerformanceWithP2PData(data, currentP2P)
 * );
 * ```
 */
export const syncPerformanceData = (
  performanceData: PerformanceData[],
  initialPerformanceData: PerformanceData[],
  updater: (data: PerformanceData) => void,
): void => {
  const current = performanceData[PERFORMANCE_DATA_INDEX];
  const initial = initialPerformanceData[PERFORMANCE_DATA_INDEX];

  if (current) updater(current);
  if (initial) updater(initial);
};
